// src/App.js
import React, { useEffect, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './index.css';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigationType, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { usePostHog } from './utils/PostHogContext';
// All pages
import Home from './pages/Home';
import Example from './pages/Example';
// import Contact from './pages/Contact';
import DemoProduct from './pages/DemoProduct';
import OnboardingPage from './pages/Onboarding/OnboardingPage'; // NEW ONBOARDING
import About from './pages/About';
import Review from './pages/Review';
import RecPrototype from './pages/RecPrototype';
import ClinicContactFloater from './components/ClinicContactFloater';
import ClinicInformation from './components/ClinicInformation';
import ClinicPhotos from './components/ClinicPhotos';
import ClinicDoctorList from './components/ClinicDoctorList';
import ScrollToTop from './components/ScrollToTop';
import Guide from './pages/Guide';
import Cta from './components/Cta';
import Footer from './components/Footer';
import NavBar from './components/Navbar/NavBar';
import ContactClinic from './components/ContactClinic';
import Clinic from './pages/Clinic';
import ThankYouPage from './components/ThankYouPage';
import { onAuthChange as onAuthStateChanged, restoreEmailOnlySession } from './firebase';
import Blog from './pages/Blog';
import useMetaPixel from './useMetaPixel';
import useTikTokPixel from './useTikTokPixel';
import BlogShowroom from './pages/BlogShowroom';
import Documents from './pages/Documents';
import ContactUs from './pages/ContactUs';
import { initializeSession, trackUserAuthentication, setupActivityTracking, setupURLTracking } from './utils/Tracking';
import { initializeUrlDataHandler } from './utils/UrlDataHandler';
import AdLanding from './pages/AdLanding';
import DownloadGuide from './pages/DownloadGuide';
import ViewReportPage from './pages/ViewReport';
import ContactInfoPage from './pages/ContactInfoPage';
import PatientPage from './pages/PatientPage';

// Component to track page changes
function PageChangeTracker() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const posthog = usePostHog();

  useEffect(() => {
    // Process URL data handler on location change
    initializeUrlDataHandler();

    // TikTok Pixel page view tracking for SPA route changes
    if (window.ttq) {
      window.ttq.page();
    }

    // PostHog page view tracking
    posthog?.capture('$pageview', {
      path: location.pathname,
      url: window.location.href,
      referrer: document.referrer
    });
  }, [location.pathname, posthog]);

  return null;
}

function App() {
  const [authChecked, setAuthChecked] = useState(false);
  const [prevAuthState, setPrevAuthState] = useState(null);
  const [currentUser, setCurrentUser] = useState(null); // Track current user (Firebase Auth or email-only)
  const posthog = usePostHog();

  // Initialize Meta Pixel with your Pixel ID
  const META_PIXEL_ID = '992885382726061';
  useMetaPixel(META_PIXEL_ID);

  // Initialize TikTok Pixel
  const TIKTOK_PIXEL_ID = 'D7F51PJC77U3RPGVH32G';
  useTikTokPixel(TIKTOK_PIXEL_ID);

  // Helper function to handle auth state changes (both Firebase Auth and email-only)
  const handleAuthStateChange = async (user, authMethod = 'session_restored') => {

    // Track when a user becomes authenticated
    if (user && prevAuthState === null) {
      // First auth state change after app initialization
      await trackUserAuthentication(user, authMethod);
      
      // Also identify user in PostHog
      posthog?.identify(user.uid, {
        email: user.email || 'unknown',
        auth_method: authMethod,
        is_email_only: user.isEmailOnly || false
      });
    } else if (user && prevAuthState === false) {
      // User just logged in during this session
      await trackUserAuthentication(user, 'login_or_signup');
      
      // Also identify user in PostHog
      posthog?.identify(user.uid, {
        email: user.email || 'unknown',
        auth_method: 'login_or_signup',
        is_email_only: user.isEmailOnly || false
      });
    }

    // Update auth state
    setCurrentUser(user);
    setPrevAuthState(user ? true : false);
    setAuthChecked(true);
    AOS.refresh();
  };

  useEffect(() => {
    // Initialize AOS immediately
    AOS.init({
      once: true,
      duration: 1000,
      easing: 'ease-out-cubic',
      initClassName: 'aos-init',
      startEvent: 'DOMContentLoaded',
    });

    // Set up Firebase Auth state listener
    const unsubscribeFirebaseAuth = onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        // Firebase Auth user found
        await handleAuthStateChange(firebaseUser, 'session_restored');
      } else {
        // No Firebase Auth user, check for email-only auth
        
        try {
          const emailOnlyUser = await restoreEmailOnlySession();
          if (emailOnlyUser) {
            await handleAuthStateChange(emailOnlyUser, 'email_only_session_restored');
          } else {
            // No auth found at all
            await handleAuthStateChange(null);
          }
        } catch (error) {
          console.error('❌ Error restoring email-only session:', error);
          // Fallback to no auth
          await handleAuthStateChange(null);
        }
      }
    });

    return () => {
      unsubscribeFirebaseAuth();
      AOS.refreshHard();
    };
  }, [prevAuthState, posthog]);

  /* Hotjar – load only when the browser is idle */
  useEffect(() => {
    import('./utils/loadWhenIdle').then(({ loadWhenIdle }) =>
      loadWhenIdle(() => {
        (function(h, o, t, j, a, r) {
          h.hj =
            h.hj ||
            function() {
              (h.hj.q = h.hj.q || []).push(arguments);
            };
          h._hjSettings = { hjid: 6437228, hjsv: 6 };
          a = o.getElementsByTagName('head')[0];
          r = o.createElement('script');
          r.async = 1;
          r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
          a.appendChild(r);
        })(
          window,
          document,
          'https://static.hotjar.com/c/hotjar-',
          '.js?sv='
        );
      })
    );
  }, []);

  // Initialize tracking system
  useEffect(() => {
    let activityCleanup;
    let urlCleanup;
    let isInitialized = false;
    
    const initTracking = async () => {
      // Prevent multiple initializations
      if (isInitialized) return;
      isInitialized = true;
      
      try {
        // Initialize session (pass user email if authenticated)
        let userEmail = null;
        // Check if user is already authenticated from previous auth state
        if (prevAuthState === true && currentUser) {
          userEmail = currentUser.email;
        }
        
        const sessionId = await initializeSession(userEmail);
        if (sessionId) {
          
          // Set up activity tracking
          activityCleanup = setupActivityTracking();
          
          // Set up URL change tracking
          urlCleanup = setupURLTracking();
          
          // Capture initial pageview in PostHog
          posthog?.capture('$pageview', {
            path: window.location.pathname,
            url: window.location.href,
            referrer: document.referrer
          });
        } else {
          console.error('❌ Failed to initialize tracking session');
        }
      } catch (error) {
        console.error('❌ Error initializing tracking:', error);
      }
    };
    
    initTracking();
    
    // Cleanup on unmount
    return () => {
      if (activityCleanup) activityCleanup();
      if (urlCleanup) urlCleanup();
    };
  }, [posthog, currentUser]);

  return (
    <Router>
      {/* Global default Helmet: Default meta tags and Google Tags */}
      <Helmet>
        <meta
          name="description"
          content="Welcome to Match Medic – Find the right hair transplant clinic for your unique needs. Cut through the noise and misinformation surrounding hair transplants. Our algorithm will recommend the clinics with the best track record for your unique needs within seconds."
        />
        {/* Google tag for Google Ads (AW) and Google Analytics (GA4) */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=AW-16895010584"></script>
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-4Z48RHHR20"></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            
            // Google Ads tag
            gtag('config', 'AW-16895010584');
            
            // Google Analytics tag
            gtag('config', 'G-4Z48RHHR20');
          `}
        </script>
      </Helmet>
      <PageChangeTracker />
      <ScrollToTop />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/about" element={<About />} />
          {/* Redirect /recommendations to home page */}
          {/* <Route path="/recommendations" element={<Navigate to="/onboarding" replace />} /> */}
          <Route path="/recommendations/:recommendationId" element={<RecPrototype />} />
          {/* <Route path="/rec_prototype/:recommendationId" element={<RecPrototype />} /> */}
          <Route path="/guide" element={<Guide />} />
          {/* <Route path="/contact-clinic" element={<ContactClinic />} /> */}
          <Route path="/blog/:postId" element={<Blog />} />
          {/* <Route path="/thank-you" element={<ThankYouPage />} /> */}
          {/* Dynamic Route for Clinics */}
          {/* <Route path="/clinic/:clinicName" element={<Clinic />} /> */}
          <Route path="/review/:reviewId" element={<Review />} />
          <Route path="/blog-showroom" element={<BlogShowroom />} />
          <Route path="/contact" element={<ContactUs />} />
          {/* New route for dynamic document loading */}
          <Route path="/documents/:documentId" element={<Documents />} />
          {/* <Route path="/adLanding" element={<AdLanding />} /> */}
          {/* <Route path="/reviews" element={<Home />} /> */}
          <Route path="/download-guide" element={<DownloadGuide />} />
          {/* View Report - password-protected report viewing */}
          <Route path="/view-report/:requestId" element={<ViewReportPage />} />
          {/* Contact Info - standalone contact collection page */}
          <Route path="/contact-info" element={<ContactInfoPage />} />
          {/* Patient record - clinic-facing patient profile page */}
          <Route path="/patient/:documentId" element={<PatientPage />} />

        </Routes>
    </Router>
  );
}

export default App;